/**
 * Port of {@class CelSuggestionReplacements} —
 * backend/src/main/java/com/irian/iam/ruleengine/cel/CelSuggestionReplacements.java
 *
 * Single source of truth for the Replacement a suggestion's Kind should carry
 * when the engine hasn't built one explicitly, and for the trailing-whitespace
 * cleanup pass that runs after replacements are built.
 */

import {Kind, type CelSuggestion, type Replacement, celSuggestion, replacementOf, withReplacement} from './CelSuggestion'

const NULL_LITERAL = 'null'
const SIZE = 'size'

/**
 * Sentinel identifier injected at the cursor offset so a partial source
 * (`pilot.|`) becomes a parseable expression (`pilot.__cursor__`).
 */
export const CURSOR = '__cursor__'

/**
 * CEL standard library — functions and macros callable at expression start.
 * cel-java does not expose the stdlib registry as an iterable, so this is the
 * hardcoded surface (cel-spec § 4).
 */
export const CEL_STDLIB: readonly string[] = [
    SIZE, 'int', 'uint', 'double', 'string', 'bool', 'bytes',
    'type', 'timestamp', 'duration', 'dyn', 'has',
    'cel.bind',
]

export const SCALAR_NUMERIC_OPS: readonly string[] = ['==', '!=', '<', '<=', '>', '>=', 'in']
export const SCALAR_STRING_OPS: readonly string[] = ['==', '!=', 'in']
export const SCALAR_BOOL_OPS: readonly string[] = ['==', '!=']
export const COLLECTION_OPS: readonly string[] = ['==', '!=', 'in']

/** Ordered list of CEL list/macro methods (display + emission order). */
export const CEL_LIST_METHODS: readonly string[] = [SIZE, 'exists', 'all', 'filter', 'map', 'exists_one']

/**
 * Ordered list of CEL string methods. Includes `size` since "abc".size() is
 * legal CEL — overlaps with CEL_LIST_METHODS.
 */
export const CEL_STRING_METHODS: readonly string[] = ['startsWith', 'endsWith', 'contains', 'matches', SIZE]

export const OPERATOR_DISPLAYS: ReadonlySet<string> = new Set(['==', '!=', '<', '<=', '>', '>=', 'in'])
export const LOGICAL_DISPLAYS: ReadonlySet<string> = new Set(['&&', '||', '!', '('])
export const CLOSER_DISPLAYS: ReadonlySet<string> = new Set([')', ']'])
export const CEL_LIST_METHOD_DISPLAYS: ReadonlySet<string> = new Set(CEL_LIST_METHODS)
export const CEL_STRING_METHOD_DISPLAYS: ReadonlySet<string> = new Set(CEL_STRING_METHODS.slice(0, CEL_STRING_METHODS.length - 1))

/**
 * Schema-agnostic display-to-Kind mapping.
 */
export function classifyKind(text: string, valuePos: boolean, inOpenString: boolean): Kind {
    if (OPERATOR_DISPLAYS.has(text)) return Kind.OPERATOR
    if (LOGICAL_DISPLAYS.has(text)) return Kind.LOGICAL
    if (CLOSER_DISPLAYS.has(text)) return Kind.CLOSER
    if (CEL_LIST_METHOD_DISPLAYS.has(text)) return Kind.LIST_MACRO
    if (CEL_STRING_METHOD_DISPLAYS.has(text)) return Kind.STRING_METHOD
    if (inOpenString) return Kind.VALUE
    if (text.includes('.')) return Kind.STATIC_FIELD
    if (valuePos) return Kind.VALUE
    return Kind.ROOT_KEYWORD
}

export class CelSuggestionReplacements {
    /**
     * Build replacements for every suggestion in raw that doesn't already
     * carry one, then trim a trailing replacement-space when the cursor is
     * followed by whitespace.
     */
    applyDefaults(raw: readonly CelSuggestion[], source: string, cursor: number): CelSuggestion[] {
        const inString = this.inOpenStringLiteral(source, cursor)
        const out: CelSuggestion[] = []
        for (const s of raw) {
            if (s.replacement !== null) {
                out.push(s)
                continue
            }
            const r = this.defaultFor(s.text, s.kind, source, cursor)
            if (s.kind === Kind.VALUE && !inString && s.text !== NULL_LITERAL) {
                const quoted = `'${s.text}'`
                out.push(celSuggestion(quoted, s.kind, s.valueType, s.description, r))
            } else {
                out.push(withReplacement(s, r))
            }
        }
        const afterText = source.substring(Math.min(cursor, source.length))
        if (afterText.length > 0 && /\s/.test(afterText.charAt(0))) {
            const trimmed: CelSuggestion[] = []
            for (const s of out) {
                const r = s.replacement
                if (r !== null && r.text.endsWith(' ')) {
                    trimmed.push(withReplacement(s, replacementOf(r.start, r.end, r.text.substring(0, r.text.length - 1))))
                } else {
                    trimmed.push(s)
                }
            }
            return trimmed
        }
        return out
    }

    defaultFor(text: string, kind: Kind, source: string, cursor: number): Replacement {
        if (cursor < 0 || cursor > source.length) {
            throw new Error(`cursor ${cursor} out of range for source of length ${source.length}`)
        }
        if (kind === Kind.VALUE) {
            return this.valueReplacement(text, source, cursor)
        }
        return this.spliceReplacement(text, kind, source, cursor)
    }

    private valueReplacement(text: string, source: string, cursor: number): Replacement {
        const openQuote = this.openQuoteChar(source, cursor)
        if (openQuote !== '') {
            let start = cursor
            while (start > 0 && isIdentChar(source.charAt(start - 1))) start--
            let end = cursor
            while (end < source.length && isIdentChar(source.charAt(end))) end++
            const closed = end < source.length && source.charAt(end) === openQuote
            return replacementOf(start, end, closed ? text : text + openQuote)
        }
        const quoted = text === NULL_LITERAL ? text : `'${text}'`
        return replacementOf(cursor, cursor, quoted)
    }

    private spliceReplacement(text: string, kind: Kind, source: string, cursor: number): Replacement {
        const back = this.longestSuffixThatIsPrefixOf(source.substring(0, cursor), text)
        const fwd = this.longestPrefixThatIsSuffixOf(source.substring(cursor), text.substring(back))
        let body = text
        switch (kind) {
            case Kind.LIST_MACRO:
            case Kind.STRING_METHOD:
                body = text + '('
                break
            case Kind.OPERATOR:
                body = text + ' '
                break
            case Kind.LOGICAL:
                body = (text === '&&' || text === '||') ? text + ' ' : text
                break
            default:
                body = text
        }
        return replacementOf(cursor - back, cursor + fwd, body)
    }

    /**
     * Apply this defaulter's splice to produce the post-acceptance string.
     */
    splice(source: string, cursor: number, text: string): string {
        const r = this.defaultFor(text, Kind.STATIC_FIELD, source, cursor)
        return source.substring(0, r.start) + r.text + source.substring(r.end)
    }

    longestSuffixThatIsPrefixOf(head: string, text: string): number {
        for (let n = Math.min(head.length, text.length); n > 0; n--) {
            if (text.startsWith(head.substring(head.length - n))) return n
        }
        return 0
    }

    longestPrefixThatIsSuffixOf(tail: string, remainder: string): number {
        for (let n = Math.min(tail.length, remainder.length); n > 0; n--) {
            if (remainder.endsWith(tail.substring(0, n))) return n
        }
        return 0
    }

    /**
     * True when the cursor sits inside an unterminated string literal —
     * single or double quoted.
     */
    inOpenStringLiteral(source: string, cursor: number): boolean {
        return this.openQuoteChar(source, cursor) !== ''
    }

    /**
     * The quote character of the unterminated string literal the cursor sits
     * inside, or the empty string when the cursor is not in an open string.
     */
    openQuoteChar(source: string, cursor: number): string {
        let openQuote: string = ''
        for (let i = 0; i < cursor; i++) {
            const c = source.charAt(i)
            if (openQuote !== '' && c === '\\' && i + 1 < cursor) {
                i++
                continue
            }
            if (openQuote === '') {
                if (c === "'" || c === '"') openQuote = c
            } else if (c === openQuote) {
                openQuote = ''
            }
        }
        return openQuote
    }
}

function isIdentChar(c: string): boolean {
    return /[A-Za-z0-9_]/.test(c)
}
